import React, { useRef, useEffect, useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { ICommentThreadObject, ITableViewColumn, IUpdateTableViewCell } from 'types'
import Cell from 'components/spreadsheet/components/cell'
import Header from 'components/spreadsheet/components/header'
import Editor from 'components/editor'
import CommentThreads from 'components/comments/commentThreads'
import Tooltip from 'components/tooltip'
import { Comment } from 'components/icons'
import { PERMISSIONS } from 'helpers/auth'
import { safeLocalStorage } from 'helpers/localStorage'
import { rowHeights, ISelectedCellState } from 'components/spreadsheet/types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getRowValue, isColumnLockedByRules, rowDataArrayToObject } from 'components/spreadsheet/helpers/functions'
import Button from 'components/button'

// Persist the user's last comments-panel state so it reopens the way they left it. Collapsed by default.
const COMMENTS_PANEL_STORAGE_KEY = 'morta.rowDetails.commentsExpanded'

interface ExpandedRowProps extends ModalProps {
  focusInput: boolean
  selectedCell: ISelectedCellState
  setSelectedCell: (selectedCell: ISelectedCellState) => void
  setCellValue: (cells: IUpdateTableViewCell, onSuccess: () => void, onError: () => void) => void
  initialRowId: string | undefined
  initialRowNumber: number | undefined
}

const ExpandedRow: React.FC<ExpandedRowProps> = ({
  id,
  open,
  setOpen,
  setCellValue,
  focusInput,
  selectedCell,
  setSelectedCell,
  onClose,
  initialRowId,
  initialRowNumber
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const [rowId, setRowId] = useState<string>()
  const [rowNumber, setRowNumber] = useState<number>()
  // Collapsed by default, remembering the user's last choice. When the row was opened
  // via "Add Comment" (focusInput) we force the panel open so they can comment straight away.
  const [showComments, setShowComments] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search)
    return (
      focusInput ||
      (params.get('row') === initialRowId && params.has('commentThread')) ||
      safeLocalStorage.getItem(COMMENTS_PANEL_STORAGE_KEY) === 'true'
    )
  })

  const toggleComments = (): void => {
    setShowComments((previous) => {
      const next = !previous
      safeLocalStorage.setItem(COMMENTS_PANEL_STORAGE_KEY, String(next))
      return next
    })
  }

  useEffect(() => {
    if (focusInput === true) inputRef.current?.focus()
  }, [])

  useEffect(() => {
    setRowId(initialRowId)
    setRowNumber(initialRowNumber)
  }, [])

  const handleCommentThreadsChange = (value: ICommentThreadObject[]) => {
    if (rowId) {
      const noResolvedCommentThreads = value.filter(
        (comment: ICommentThreadObject) => comment.resolvedAt !== null
      ).length
      const noOpenCommentThreads =
        value.filter((comment: ICommentThreadObject) => comment.resolvedAt === null).length + noResolvedCommentThreads

      setSpreadsheetData({
        type: 'COMMENT_THREADS_CHANGE',
        rowId: rowId,
        noOpenCommentThreads,
        noResolvedCommentThreads
      })
    }
  }

  // Comment counts for this row (kept in the view stats) so the toggle can show a badge
  // without the panel being open. `openCommentThreads` holds the total thread count.
  const rowCommentStats = rowId ? spreadsheetData.comments.find((stats) => stats.referenceId === rowId) : undefined
  const totalCommentThreads = rowCommentStats?.openCommentThreads ?? 0
  const hasUnresolvedComments = rowCommentStats
    ? rowCommentStats.openCommentThreads - rowCommentStats.resolvedCommentThreads > 0
    : false

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      title={rowNumber !== undefined ? `Row #${rowNumber + 1}` : ''}
      headerDivider
      removePadding
      fillHeight
      headerActions={
        spreadsheetData.tableDetails.allowComments ? (
          <>
            <Tooltip content={showComments ? 'Hide comments' : 'Show comments'}>
              <span className="relative flex">
                <Button
                  type="button"
                  internalType="transparent"
                  aria-label={showComments ? 'Hide comments' : 'Show comments'}
                  aria-pressed={showComments}
                  onClick={toggleComments}
                  className="flex items-center justify-center w-8 h-8 [&_svg]:w-6 [&_svg]:h-6 p-1.5"
                >
                  <Comment />
                </Button>
                {totalCommentThreads > 0 && (
                  <span
                    aria-hidden="true"
                    className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[13px] font-semibold leading-none pointer-events-none text-body ${
                      hasUnresolvedComments ? 'bg-light-pink' : 'bg-light-blue'
                    }`}
                  >
                    {totalCommentThreads}
                  </span>
                )}
              </span>
            </Tooltip>
            {/* Vertical separator between the comments toggle and the modal close button */}
            <div className="w-px h-6 bg-surface-strong ml-3" />
          </>
        ) : undefined
      }
    >
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div
          className={`flex flex-col overflow-y-auto min-h-0 flex-1 min-w-0 py-[30px] pl-10 ${
            spreadsheetData.tableDetails.allowComments && showComments ? 'pr-6' : 'pr-10'
          }`}
        >
          {spreadsheetData.viewDetails.columns
            .filter(
              (column) => !spreadsheetData.userConfiguration.hiddenColumns.find((hidden) => hidden === column.publicId)
            )
            .map((column: ITableViewColumn, columnNumber: number) => {
              const row = spreadsheetData.rows.find((row) => row.publicId === rowId)
              const value = row
                ? getRowValue(row, column.publicId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
                : null
              const selected = selectedCell.columnId === column.publicId && selectedCell.rowId === rowId

              return (
                <div
                  key={columnNumber}
                  className="flex flex-col bg-surface-muted border border-divider rounded-lg mb-5 p-2.5"
                >
                  <Header
                    columnNumber={columnNumber}
                    columnId={column.publicId}
                    value={column.name}
                    kind={column.kind}
                    backgroundColor="var(--color-surface-muted)"
                    border={`0px`}
                    frozen={false}
                    isLastFrozenColumn={false}
                    position="relative"
                  />
                  {column.description && (
                    <div className="mt-2.5 mb-2.5 pt-2.5 pb-2.5 leading-normal">
                      <Editor
                        databaseDoc={column.description}
                        readOnly={true}
                        editorId={spreadsheetData.tableDetails.publicId}
                        border={false}
                        resources={[{ resource: 'document_table', publicId: spreadsheetData.tableDetails.publicId }]}
                      />
                    </div>
                  )}
                  <Cell
                    kind={column.kind}
                    scriptEnabled={column.scriptEnabled}
                    formulaEnabled={column.formulaEnabled}
                    isJoined={column.isJoined}
                    value={value}
                    frozen={false}
                    rowHeight={rowHeights.Small}
                    isContributor={spreadsheetData.isContributor}
                    isAdmin={spreadsheetData.isAdmin}
                    uniqueNumber={Math.random()}
                    isExpanded={true}
                    setCellValue={(cells, onSuccess, onError) => {
                      if (setCellValue && setSpreadsheetData) {
                        cells.cells.forEach((cell) => {
                          if (cell.oldValue !== undefined) {
                            setSpreadsheetData({
                              type: 'EDIT_CELL',
                              columnId: cell.columnId,
                              rowId: cell.rowId,
                              value: { [cell.columnId]: cell.value as string },
                              oldValue: cell.oldValue
                            })
                          }
                        })

                        setCellValue(cells, onSuccess, onError)
                      }
                    }}
                    columnName={column.name}
                    columnNumber={columnNumber}
                    columnId={column.publicId}
                    rowId={rowId}
                    rowNumber={rowNumber}
                    setSelectedCell={setSelectedCell}
                    selected={selected}
                    editing={selected && selectedCell.editing}
                    tempValue={selected ? selectedCell.value : undefined}
                    locked={
                      isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) ||
                      column.isJoined ||
                      column.isSynced
                    }
                    rowData={
                      row
                        ? rowDataArrayToObject(
                            row.rowData,
                            spreadsheetData.indexToColumnId,
                            row._rowIndex,
                            spreadsheetData.dataVersion >= 0 ? spreadsheetData.columnStore : null
                          )
                        : undefined
                    }
                    columnValuesCount={spreadsheetData.columnValuesCount}
                    colourSettings={spreadsheetData.userConfiguration.colourSettings}
                    type={spreadsheetData.viewDetails.type}
                    column={column}
                    startRowId={spreadsheetData.rows[0]?.publicId}
                    finalRowId={spreadsheetData.rows[spreadsheetData.rows.length - 1]?.publicId}
                    finalRowNumber={
                      spreadsheetData.visibleBitmap ? spreadsheetData.visibleCount : spreadsheetData.rows.length
                    }
                    startColumnId={spreadsheetData.viewDetails.columns[0]?.publicId}
                    finalColumnId={
                      spreadsheetData.viewDetails.columns[spreadsheetData.viewDetails.columns.length - 1].publicId
                    }
                    finalColumnNumber={spreadsheetData.viewDetails.columns.length}
                    allowComments={spreadsheetData.tableDetails.allowComments}
                    row={row === undefined ? null : row}
                  />
                </div>
              )
            })}
        </div>

        {spreadsheetData.tableDetails.allowComments && showComments && (
          <>
            {/* Full-height separator between the row details and the comments panel */}
            <div className="w-px bg-surface-strong flex-none" />
            <div id="row-comments" className="flex-none w-[380px] max-w-[40%] min-h-0 overflow-hidden pb-[30px]">
              {rowId && (
                <CommentThreads
                  referenceType={'table'}
                  referenceId={rowId}
                  selected={focusInput}
                  mainReferenceId={spreadsheetData.tableDetails.publicId}
                  permissionLevel={spreadsheetData.isContributor ? PERMISSIONS.contributor : PERMISSIONS.viewer}
                  onOpenCommentTreadsChange={() => {
                    return null
                  }}
                  onCommentThreadsChange={(value) => handleCommentThreadsChange(value)}
                  spreadsheetData={spreadsheetData}
                  onCollapse={toggleComments}
                />
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default ExpandedRow
